import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import ChangePassword from "./components/ChangePassword";
import axios from "axios";
import Login from "./components/Login";
import Register from "./components/Register";
import Dashboard from "./components/Dashboard";
import SubtitleEditor from "./components/SubtitleEditor";
import Admin from "./components/Admin";
import TimeTracking from "./components/TimeTracking";
import AdminTimeTracking from "./components/AdminTimeTracking";
import { getApiBaseUrl } from "./utils/apiConfig";

import Header from "./components/Header";
import "./App.css";

// Set up axios defaults with flexible port detection
let apiBaseUrl = null;
const initializeAxios = async () => {
  try {
    apiBaseUrl = await getApiBaseUrl();
    axios.defaults.baseURL = apiBaseUrl;
  } catch (error) {
    console.error("Failed to initialize API base URL:", error);
    // Fallback to default
    axios.defaults.baseURL = "http://localhost:3001";
  }
};

// Initialize axios configuration
initializeAxios();

// Add token to requests if available
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = (userData, token) => {
    setUser(userData);
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className="App">
        {user && <Header user={user} onLogout={logout} />}
        <main className="main-content">
          <Routes>
            <Route
              path="/change-password"
              element={
                user ? (
                  <ChangePassword
                    user={user}
                    onPasswordChanged={() => {
                      const updated = { ...user, must_change_password: false };
                      setUser(updated);
                      localStorage.setItem("user", JSON.stringify(updated));
                    }}
                  />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/login"
              element={
                user ? (
                  user.must_change_password ? (
                    <Navigate to="/change-password" />
                  ) : ["director", "operator", "actor"].includes(
                      user.user_type
                    ) ? (
                    <Navigate to="/time-tracking" />
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                ) : (
                  <Login onLogin={login} />
                )
              }
            />
            <Route
              path="/register"
              element={
                user ? (
                  user.must_change_password ? (
                    <Navigate to="/change-password" />
                  ) : ["director", "operator", "actor"].includes(
                      user.user_type
                    ) ? (
                    <Navigate to="/time-tracking" />
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                ) : (
                  <Register onRegister={login} />
                )
              }
            />
            <Route
              path="/dashboard"
              element={
                user ? (
                  user.must_change_password ? (
                    <Navigate to="/change-password" />
                  ) : ["director", "operator", "actor"].includes(
                      user.user_type
                    ) ? (
                    <Navigate to="/time-tracking" />
                  ) : (
                    <Dashboard user={user} />
                  )
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/admin"
              element={
                user && user.user_type === "admin" ? (
                  user.must_change_password ? (
                    <Navigate to="/change-password" />
                  ) : (
                    <Admin user={user} />
                  )
                ) : (
                  <Navigate to="/dashboard" />
                )
              }
            />
            <Route
              path="/time-tracking"
              element={
                user &&
                ["director", "operator", "actor"].includes(user.user_type) ? (
                  user.must_change_password ? (
                    <Navigate to="/change-password" />
                  ) : (
                    <TimeTracking user={user} />
                  )
                ) : (
                  <Navigate to="/dashboard" />
                )
              }
            />
            <Route
              path="/admin/time-tracking"
              element={
                user && user.user_type === "admin" ? (
                  user.must_change_password ? (
                    <Navigate to="/change-password" />
                  ) : (
                    <AdminTimeTracking user={user} />
                  )
                ) : (
                  <Navigate to="/dashboard" />
                )
              }
            />

            <Route
              path="/editor/:projectId"
              element={
                user &&
                !["director", "operator", "actor"].includes(user.user_type) ? (
                  <SubtitleEditor user={user} />
                ) : (
                  <Navigate to="/dashboard" />
                )
              }
            />
            <Route
              path="/"
              element={
                <Navigate
                  to={
                    user
                      ? user.must_change_password
                        ? "/change-password"
                        : ["director", "operator", "actor"].includes(
                            user.user_type
                          )
                        ? "/time-tracking"
                        : "/dashboard"
                      : "/login"
                  }
                />
              }
            />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;
